import { useRef, useState } from 'react';
import { contact } from '../Data/Data';

export const emptyForm = { first: '', last: '', email: '', comment: '', website: '' };

// State for the contact form: fields, honeypot, fill-time stamp, Turnstile token,
// and the submit lifecycle. Framework glue only; all enforcement is server-side.
export function useContactForm({ fetchImpl, now = Date.now } = {}) {
    const [form, setForm] = useState(emptyForm);
    const [status, setStatus] = useState('idle'); // idle | sending | sent | failed
    const [errorMessage, setErrorMessage] = useState('');
    const [token, setToken] = useState(null);
    const startedAt = useRef(null);
    const turnstileRef = useRef(null);

    const doFetch = fetchImpl ?? ((...args) => fetch(...args));

    const update = (e) => {
        if (startedAt.current === null) startedAt.current = now();
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    };

    // Reset the widget and token so the next attempt gets a fresh single-use
    // token. The fill clock is deliberately kept: a retry is older, not newer.
    const resetChallenge = () => {
        turnstileRef.current?.reset?.();
        setToken(null);
    };

    const challengeFailed = () => {
        setToken(null);
        setErrorMessage(contact.failed);
        setStatus('failed');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!token) {
            setErrorMessage(contact.botCheck);
            setStatus('failed');
            return;
        }
        setStatus('sending');
        setErrorMessage('');
        try {
            const response = await doFetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...form,
                    elapsedMs: startedAt.current === null ? 0 : now() - startedAt.current,
                    turnstileToken: token,
                }),
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                const limited = response.status === 403 || response.status === 429;
                setErrorMessage(data.message || (limited ? contact.tooMany : ''));
                setStatus('failed');
                resetChallenge();
                return;
            }
            setForm(emptyForm);
            setStatus('sent');
            resetChallenge();
            startedAt.current = null;
        } catch (error) {
            console.error('Error:', error);
            setErrorMessage('');
            setStatus('failed');
            resetChallenge();
        }
    };

    return { form, status, errorMessage, token, setToken, challengeFailed, update, handleSubmit, turnstileRef };
}
